import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, Copy, RefreshCw, Check, Phone, Mail, Presentation, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton, InlineError } from "@/components/common";
import { EvidenceLabel, LANGS, langLabel, SectionLabel } from "@/components/intel/primitives";

const TABS = [
  { key: "brief", label: "Call brief", Icon: Phone, testId: "copilot-tab-call-brief" },
  { key: "outreach", label: "Outreach e-mail", Icon: Mail, testId: "copilot-tab-outreach" },
  { key: "meeting", label: "Meeting prep", Icon: Presentation, testId: "copilot-tab-meeting-prep" },
];

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

function Block({ label, kind, children, className, testId }) {
  if (!children) return null;
  return (
    <section className={cn("py-4", className)} data-testid={testId}>
      <SectionLabel right={kind ? <EvidenceLabel kind={kind} /> : null}>{label}</SectionLabel>
      <div className="text-[13.5px] leading-relaxed text-foreground/90">{children}</div>
    </section>
  );
}

function List({ items }) {
  if (!items || !items.length) return <span className="text-muted-foreground">Unknown</span>;
  return (
    <ul className="space-y-1.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2.5">
          <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-foreground/50" />
          <span>{typeof it === "string" ? it : JSON.stringify(it)}</span>
        </li>
      ))}
    </ul>
  );
}

function Loading({ label }) {
  return (
    <div className="space-y-5 py-4" data-testid="copilot-loading">
      <div className="flex items-center gap-2 text-[13px] text-muted-foreground"><Sparkles className="h-4 w-4 animate-pulse text-primary" /> {label}</div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-[92%]" />
          <Skeleton className="h-4 w-[70%]" />
        </div>
      ))}
    </div>
  );
}

function BriefView({ data }) {
  return (
    <div className="divide-y divide-black/[0.05]">
      <Block label="Why we are calling" kind="hypothesis">{data.why_we_are_calling}</Block>
      <Block label="What we know" kind="signal"><List items={data.what_we_know} /></Block>
      <Block label="What we do not know"><List items={data.what_we_do_not_know} /></Block>
      <Block label="Commercial hypothesis" kind="hypothesis">{data.commercial_hypothesis}</Block>
      <Block label="Relevant Mediability capabilities" kind="inference">
        <div className="space-y-2">
          {(data.relevant_capabilities || []).map((c, i) => (
            <div key={i} className="rounded-lg border border-black/[0.06] bg-[hsl(40_12%_98.5%)] px-3 py-2">
              <div className="text-[13px] font-semibold">{c.category}</div>
              <div className="text-[12.5px] text-muted-foreground">{c.why}</div>
            </div>
          ))}
        </div>
      </Block>
      <Block label="Discovery questions"><List items={data.discovery_questions} /></Block>
      <Block label="Likely objections">
        <div className="space-y-2.5">
          {(data.likely_objections || []).map((o, i) => (
            <div key={i}>
              <div className="text-[13px] font-semibold">“{o.objection}”</div>
              <div className="text-[12.5px] text-muted-foreground">{o.response}</div>
            </div>
          ))}
        </div>
      </Block>
      <Block label="Ideal next step">{data.ideal_next_step}</Block>
    </div>
  );
}

function MeetingView({ data }) {
  return (
    <div className="divide-y divide-black/[0.05]">
      <Block label="Meeting objective">{data.meeting_objective}</Block>
      <Block label="Company overview" kind="fact">{data.company_overview}</Block>
      <Block label="Latest signals" kind="signal"><List items={data.latest_signals} /></Block>
      <Block label="Project hypothesis" kind="hypothesis">{data.project_hypothesis}</Block>
      <Block label="What we know"><List items={data.what_we_know} /></Block>
      <Block label="What we need to learn"><List items={data.what_we_need_to_learn} /></Block>
      <Block label="Relevant solutions" kind="inference">
        <div className="space-y-2">
          {(data.relevant_solutions || []).map((c, i) => (
            <div key={i} className="rounded-lg border border-black/[0.06] bg-[hsl(40_12%_98.5%)] px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-[13px] font-semibold">{c.category}</span>
                {c.vendors?.length ? <span className="text-[11.5px] text-muted-foreground">{c.vendors.join(" · ")}</span> : null}
              </div>
              <div className="text-[12.5px] text-muted-foreground">{c.why}</div>
            </div>
          ))}
        </div>
      </Block>
      <Block label="Discovery questions"><List items={data.discovery_questions} /></Block>
      <Block label="Technical considerations"><List items={data.technical_considerations} /></Block>
      <Block label="Likely stakeholders" kind="inference">
        <div className="space-y-1.5">
          {(data.likely_stakeholders || []).map((s, i) => (
            <div key={i} className="flex gap-2.5"><span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-foreground/50" /><span><span className="font-semibold">{s.role}</span> <span className="text-muted-foreground">— {s.why}</span></span></div>
          ))}
        </div>
      </Block>
    </div>
  );
}

function OutreachView({ data, onApprove, approved }) {
  const e = data;
  return (
    <div className="space-y-4 py-4">
      <div className="flex items-start gap-2.5 rounded-xl border border-primary/20 bg-[hsl(10_76%_61%/0.05)] px-3.5 py-2.5 text-[12.5px] leading-snug text-foreground/85" data-testid="copilot-approval-note">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>Review before use. Nothing is sent automatically — approve to copy the e-mail into your own mail client.</span>
      </div>
      <div className="rounded-xl border border-black/[0.07] bg-white" data-testid="copilot-outreach-email">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-black/[0.06] px-4 py-2.5 text-[12px] text-muted-foreground">
          <span>To: <span className="font-medium text-foreground/85">{e.role || "Decision maker"}</span></span>
          <span className="font-mono">{e.language}</span>
        </div>
        <div className="border-b border-black/[0.06] px-4 py-2.5">
          <div className="eyebrow text-[10px]">Subject</div>
          <div className="mt-0.5 text-[14px] font-semibold" data-testid="copilot-outreach-subject">{e.subject}</div>
        </div>
        <div className="whitespace-pre-wrap px-4 py-3.5 text-[13.5px] leading-relaxed" data-testid="copilot-outreach-body">{e.body}</div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-black/[0.06] bg-[hsl(40_12%_98.5%)] px-3 py-2.5">
          <SectionLabel right={<EvidenceLabel kind="signal" compact />}>Referenced signal</SectionLabel>
          <div className="text-[12.5px] leading-snug text-foreground/85">{e.referenced_signal}</div>
        </div>
        <div className="rounded-lg border border-black/[0.06] bg-[hsl(40_12%_98.5%)] px-3 py-2.5">
          <SectionLabel right={<EvidenceLabel kind="inference" compact />}>Why this angle</SectionLabel>
          <div className="text-[12.5px] leading-snug text-foreground/85">{e.why_this_angle}</div>
        </div>
      </div>
      {approved ? (
        <div className="flex items-center gap-2 text-[12.5px] font-medium text-[hsl(var(--status-ok))]" data-testid="copilot-outreach-approved"><CheckCircle2 className="h-4 w-4" /> Approved and copied. Logged as outreach on this company.</div>
      ) : null}
    </div>
  );
}

/**
 * Right-side Copilot. ctx = { companyId, projectId, companyName, projectName, language, roles:[{role,why}], tab }
 */
export function CopilotSheet({ open, onOpenChange, ctx, onActivity }) {
  const [tab, setTab] = useState("brief");
  const [lang, setLang] = useState("en");
  const [role, setRole] = useState("");
  const [cache, setCache] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [approved, setApproved] = useState(false);
  const reqRef = useRef(0);

  const roles = useMemo(() => ctx?.roles || [], [ctx]);
  const scope = ctx ? `${ctx.companyId}:${ctx.projectId || ""}` : "";

  useEffect(() => {
    if (!open || !ctx) return;
    setTab(ctx.tab || "brief");
    setLang(ctx.language || "en");
    setRole(ctx.role || roles[0]?.role || "");
    setApproved(false);
  }, [open, ctx, roles]);

  const key = useCallback((t) => (t === "outreach" ? `${scope}:outreach:${lang}:${role}` : `${scope}:${t}`), [scope, lang, role]);

  const generate = useCallback(async (t, force = false) => {
    if (!ctx) return;
    const k = key(t);
    if (!force && cache[k]) return;
    const id = ++reqRef.current;
    setLoading((l) => ({ ...l, [k]: true }));
    setErrors((e) => ({ ...e, [k]: null }));
    try {
      const body = { company_id: ctx.companyId, project_id: ctx.projectId || null };
      if (t === "outreach") Object.assign(body, { language: lang, role });
      const r = await api.post(`/intel/copilot/${t}`, body);
      if (id !== reqRef.current && t !== "outreach") { /* stale but still cache */ }
      setCache((c) => ({ ...c, [k]: r.data }));
      if (t === "outreach") setApproved(false);
      onActivity?.();
    } catch (e) {
      setErrors((er) => ({ ...er, [k]: errMsg(e) }));
    } finally {
      setLoading((l) => ({ ...l, [k]: false }));
    }
  }, [ctx, key, cache, lang, role, onActivity]);

  useEffect(() => {
    if (open && ctx) generate(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ctx, tab, lang, role]);

  const k = key(tab);
  const data = cache[k];
  const isLoading = !!loading[k];
  const error = errors[k];

  const textFor = () => {
    if (!data) return "";
    if (tab === "outreach") return `Subject: ${data.email.subject}\n\n${data.email.body}`;
    if (tab === "brief") {
      const b = data.brief;
      return [`CALL BRIEF - ${data.company} / ${data.project}`, "", "WHY WE ARE CALLING", b.why_we_are_calling, "", "WHAT WE KNOW", ...(b.what_we_know || []).map((x) => `- ${x}`), "", "WHAT WE DO NOT KNOW", ...(b.what_we_do_not_know || []).map((x) => `- ${x}`), "", "COMMERCIAL HYPOTHESIS", b.commercial_hypothesis, "", "DISCOVERY QUESTIONS", ...(b.discovery_questions || []).map((x, i) => `${i + 1}. ${x}`), "", "IDEAL NEXT STEP", b.ideal_next_step].join("\n");
    }
    const m = data.prep;
    return [`MEETING BRIEF - ${data.company} / ${data.project}`, "", "OBJECTIVE", m.meeting_objective, "", "COMPANY", m.company_overview, "", "LATEST SIGNALS", ...(m.latest_signals || []).map((x) => `- ${x}`), "", "HYPOTHESIS", m.project_hypothesis, "", "WHAT WE NEED TO LEARN", ...(m.what_we_need_to_learn || []).map((x) => `- ${x}`), "", "DISCOVERY QUESTIONS", ...(m.discovery_questions || []).map((x, i) => `${i + 1}. ${x}`)].join("\n");
  };

  const copy = async () => {
    const ok = await copyText(textFor());
    if (ok) toast.success("Copied to clipboard");
    else toast.error("Could not copy - select the text manually");
  };

  const approve = async () => {
    const ok = await copyText(textFor());
    if (!ok) { toast.error("Could not copy - select the text manually"); return; }
    try {
      await api.post(`/intel/companies/${ctx.companyId}/actions`, { type: "outreach_approved", project_id: ctx.projectId || null, note: `Outreach approved (${langLabel(lang)}) to ${role || "decision maker"}: ${data.email.subject}` });
      setApproved(true);
      toast.success("Approved and copied", { description: "Paste it into your mail client. Logged on the company timeline." });
      onActivity?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 bg-background p-0 sm:max-w-[600px]" data-testid="copilot-sheet">
        <SheetHeader className="space-y-0 border-b border-black/[0.06] bg-white px-5 pb-4 pt-5">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"><Sparkles className="h-3.5 w-3.5 text-primary" /> Sales Copilot</div>
          <SheetTitle className="mt-1 truncate pr-8 text-[18px] font-semibold tracking-[-0.01em]" data-testid="copilot-company-name">{ctx?.companyName}</SheetTitle>
          <SheetDescription className="truncate text-[12.5px]">{ctx?.projectName || "New business"} · grounded in confirmed facts and labelled signals only</SheetDescription>
          <Tabs value={tab} onValueChange={setTab} className="mt-3">
            <TabsList className="h-9 w-full justify-start rounded-xl bg-black/[0.045] p-1">
              {TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key} data-testid={t.testId} className="h-7 flex-1 gap-1.5 rounded-lg text-[12.5px] data-[state=active]:shadow-[0_1px_2px_hsl(30_12%_18%/0.08)]"><t.Icon className="h-3.5 w-3.5" /> {t.label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 scrollbar-thin" data-testid="copilot-body">
          {tab === "outreach" ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <div className="eyebrow mb-1.5 text-[10px]">Language</div>
                <Select value={lang} onValueChange={setLang}>
                  <SelectTrigger className="h-9 rounded-[10px] bg-white" data-testid="copilot-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGS.map((l) => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="eyebrow mb-1.5 text-[10px]">Recipient role</div>
                <Select value={role || "__none"} onValueChange={(v) => setRole(v === "__none" ? "" : v)}>
                  <SelectTrigger className="h-9 rounded-[10px] bg-white" data-testid="copilot-role-select"><SelectValue placeholder="Most relevant decision maker" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">Most relevant decision maker</SelectItem>
                    {roles.map((r) => <SelectItem key={r.role} value={r.role}>{r.role}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}

          {error ? (
            <div className="pt-4"><InlineError onRetry={() => generate(tab, true)} testId="copilot-error">{error}</InlineError></div>
          ) : isLoading || !data ? (
            <Loading label={tab === "outreach" ? `Writing a first e-mail in ${langLabel(lang)}…` : tab === "brief" ? "Preparing your call brief from the evidence…" : "Preparing the meeting brief…"} />
          ) : tab === "brief" ? (
            <BriefView data={data.brief} />
          ) : tab === "meeting" ? (
            <MeetingView data={data.prep} />
          ) : (
            <OutreachView data={data.email} approved={approved} />
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-black/[0.06] bg-white px-5 py-3">
          <div className="text-[11.5px] text-muted-foreground">{data?.generated_at ? `Generated ${new Date(data.generated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "AI output - verify before use"}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => generate(tab, true)} disabled={isLoading} data-testid="copilot-regenerate-button"><RefreshCw className={cn("h-3.5 w-3.5", isLoading && "animate-spin")} /> Regenerate</Button>
            {tab === "outreach" ? (
              <Button size="sm" onClick={approve} disabled={isLoading || !data} data-testid="copilot-approve-copy-button">{approved ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {approved ? "Approved" : "Approve & copy"}</Button>
            ) : (
              <Button size="sm" onClick={copy} disabled={isLoading || !data} data-testid="copilot-copy-button"><Copy className="h-3.5 w-3.5" /> Copy</Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
